import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { setField } from '../../state/slice/form'
import _ from 'lodash'
import FormField from './FormField'
import { COMMON_FORM_FIELD_PROPS, InputType } from './FORM_FIELD_PROP_TYPES'

/**
 * Custom form field for use with a complex json form object, using redux.<br/>
 * This specific form field expects that certain properties related to the field are present in the redux store,
 *  at the path provided to this component.
 * (See {@link optionalField}, {@link requiredField})
 *
 * The use of this form field allows us to code in a way where we do not need to manage any state from parent components,
 *    as each individual form field registered with the redux store is responsible for:
 * <ul>
 *   <li>Dispatching to the store only when its value is updated</li>
 *   <li>Selecting only that field's value from the store</li>
 *   <li>Performaning any validation for the form field</li>
 *   <li><i>(Additionally) - Providing a uniform look, for all form fields across the application,
 *      in compliance with Mutual of Omaha's [Digital Design Guide]{@link https://design.mutualofomaha.com/digital/}</i></li>
 * </ul>
 *
 * This pattern also helps in optimizing re-renders.
 * If no parent component is selecting the field's value from the state for additional logic,
 *  then each form field change is contained within itself, and no other components are re-rendered.
 *
 * For scenarios where the parent needs to control the value and change handling of the input
 *    in lieu of the form field connecting to the redux store, see [ControlledFormField]{@link ControlledFormField}
 * @see optionalField
 * @see requiredField
 * @see ControlledFormField
 */
const RegisteredFormField = ({
  inputType = InputType.Text,
  id,
  path,
  label = '',
  attachedLabel,
  options,
  placeholder,
  detail,
  maxLength,
  minLength,
  className = '',
  transform,
  disabled,
  dirtyOnChange = false,
  hoverDetail,
  viewMask
}) => {

  const dispatch = useDispatch()
  const {
    value,
    valid,
    disabled: fieldDisabled,
    maskOptions,
    errorDetail,
    dirty
  } = useSelector(state => _.get(state.form, path, {}))

  const [inputViewValue, setInputViewValue] = useState()

  const onChange = (e) => {
    dispatch(setField(
      `${path}.value`,
      transform ? transform(e.target.value) : e.target.value,
      false,
      false,
      false,
      // Checkboxes have no blur event wired up (see Checkbox.jsx), so onBlur never dirties them.
      // Without dirtying immediately, isValid() short-circuits to true for un-dirtied fields,
      // which would let an unchecked "required" checkbox remain (incorrectly) valid.
      dirtyOnChange || inputType === InputType.Checkbox
    ))
  }

  const onBlur = () => {
    if (!dirty) {
      dispatch(setField(`${path}.dirty`, true))
    }
    if (viewMask) {
      setInputViewValue(viewMask(value))
    }
  }

  const onFocus = () => {
    setInputViewValue(undefined)
  }

  const maskViewIfDisabled = () => {
    return (disabled || fieldDisabled) && viewMask ?
      viewMask(value) :
      undefined
  }

  const viewValue = inputViewValue || maskViewIfDisabled()

  return <FormField
    onFocus={onFocus}
    inputType={inputType}
    id={id}
    label={label}
    attachedLabel={attachedLabel}
    options={options}
    onChange={onChange}
    onBlur={onBlur}
    viewValue={viewValue}
    value={value}
    invalid={!valid && dirty}
    error={errorDetail}
    disabled={disabled || fieldDisabled}
    placeholder={placeholder}
    detail={detail}
    maxLength={maxLength}
    minLength={minLength}
    className={className}
    maskOptions={maskOptions}
    hoverDetail={hoverDetail}
  />
}

RegisteredFormField.propTypes = {
  ...COMMON_FORM_FIELD_PROPS,
  /** Full path in the redux store object where this form value resides (required) */
  path: PropTypes.string.isRequired,
  /** A form field can be disabled by the parent, overriding the redux form field disabled property */
  disabled: PropTypes.bool,
  /** A form field can be dirtied when it is first updated, instead of waiting for blur event */
  dirtyOnChange: PropTypes.bool,
  /** Additional detail can be shown when the label is hovered */
  hoverDetail: PropTypes.string,
  /** A form field can override the input mask to mask how the value is viewed in the UI without altering the input's actual value. */
  /** This is mostly useful when we want to mask/de-mask on input focus/unfocus */
  viewMask: PropTypes.func
}

export default RegisteredFormField